import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_ARRAY, LOADING_DATA_MSG } from './ConstantsUtilities';
import { getErrMessage, to } from './PromiseUtilities';
import useAlerts from '../stores/AlertsStore';

/**
 * Fetches data asynchronously with automatic race condition handling
 *
 * Features:
 * - Prevents stale data from outdated requests
 * - Manages loading/error/success states
 * - Shows error alerts automatically
 * - Safe cleanup on unmount
 *
 * @template T
 * @param {Object} config
 * @param {() => Promise<any>} config.fetchFn Data fetching function
 * @param {(result: any) => T} [config.transformData] Transform response data (default: result?.data)
 * @param {string} [config.emptyStateMessage] Message when data is empty or after error
 * @param {(data: T) => void} [config.onSuccess] Success callback
 * @param {(error: Error) => void} [config.onError] Error callback
 * @param {any[]} config.dependencies useEffect dependencies
 * @returns {{
 *   data: T,
 *   isLoading: boolean,
 *   emptyText: string | undefined,
 *   refetch: () => void
 * }}
 *
 * @example
 * const { data, isLoading, emptyText } = useAsyncDataFetch({
 *   fetchFn: () => api.get('/users'),
 *   emptyStateMessage: 'No users found',
 *   dependencies: [userId],
 * });
 */
export default function useAsyncDataFetch({
    fetchFn,
    transformData = (result) => result?.data,
    emptyStateMessage,
    onSuccess,
    onError,
    dependencies = [],
}) {
    // State
    const [data, setData] = useState(EMPTY_ARRAY);
    const [isLoading, setIsLoading] = useState(true);
    const [emptyText, setEmptyText] = useState(LOADING_DATA_MSG);

    // Refs for race condition and unmount handling
    const requestIdRef = useRef(0);
    const isMountedRef = useRef(true);

    // Store hooks
    const [, alertsActions] = useAlerts();

    const isRequestStale = useCallback((requestId) => {
        return requestId !== requestIdRef.current || !isMountedRef.current;
    }, []);

    const updateLoadingState = useCallback(() => {
        if (!isMountedRef.current) {
            return;
        }

        setIsLoading(true);
        setEmptyText(LOADING_DATA_MSG);
        setData(EMPTY_ARRAY);
    }, []);

    const updateErrorState = useCallback(
        (error) => {
            setIsLoading(false);
            setEmptyText(emptyStateMessage);
            alertsActions.createErrorAlert(getErrMessage(error));

            if (onError) {
                onError(error);
            }
        },
        [emptyStateMessage, onError, alertsActions]
    );

    const updateSuccessState = useCallback(
        (transformedData) => {
            setIsLoading(false);
            setEmptyText(emptyStateMessage);
            setData(transformedData);

            if (onSuccess) {
                onSuccess(transformedData);
            }
        },
        [emptyStateMessage, onSuccess]
    );

    const executeFetch = useCallback(async () => {
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;

        updateLoadingState();

        const [error, response] = await to(fetchFn());

        if (isRequestStale(currentRequestId)) {
            return;
        }

        if (error) {
            updateErrorState(error);
            return;
        }

        const transformedData = transformData(response);
        updateSuccessState(transformedData);
    }, [fetchFn, transformData, isRequestStale, updateLoadingState, updateErrorState, updateSuccessState]);

    useEffect(() => {
        executeFetch().catch(() => {
            // Error handling is done inside executeFetch
        });

        return () => {
            requestIdRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const refetch = useCallback(() => {
        executeFetch().catch(() => {
            // Error handling is done inside executeFetch
        });
    }, [executeFetch]);

    return {
        data,
        isLoading,
        emptyText,
        refetch,
    };
}
